import { BearingEnvelope, FloorPolylineSegmentGeometry, RoofPlane } from '@paragon/contract-library';
import { Line2D, LineSegment2D } from '@paragon/geometry-library';
import { LineSegment2DInterface } from '@paragon/geometry-library/source/scripts/LineSegment2D';
import { getEnvelopeSegment, getEnvelopeSegmentFromDefiningSegment } from '@paragon/layout';

import { EntitySnapTarget, isBearingEnvelopeSnapType } from '../layout-snapping/EntitySnapTarget';

/**
  A utility for creating useful things from a picked entity snap target. Returns a `SnapDerivedGeometry` which can
    easily be converted to a roof plane or floor polyline segment geometry.

  @param options.primaryBearingSide - Determines how the geometry should be oriented. `flipped` will be set if the
                                        chosen bearing side is not the primary bearing side.
  @param options.bearingEnvelopes - A dictionary of bearing envelopes, needed for creating bearing-relative geometry.
  @param options.defaultElevation - The default elevation to use if an absolute geometry is created. Optional because it
                                      doesn't matter for floor polyline segments, and a default value of 0 is used for
                                      roof planes.
  @param options.forceAbsolute - If true, an absolute geometry is always returned, even when the snap targets a bearing
                                   envelope edge. Useful for letting the user override the default bearing-relative
                                   behavior with a modifier key (e.g. Shift) when they want an explicit absolute line
                                   with an editable elevation.
*/
export function createSnapDerivedGeometry(
  snap: EntitySnapTarget,
  options: {
    primaryBearingSide: 'front' | 'back';
    bearingEnvelopes: Record<string, BearingEnvelope>;

    defaultElevation?: number;
    forceAbsolute?: boolean;
  },
): SnapDerivedGeometry {
  if (options.forceAbsolute) {
    return new AbsoluteSnapDerivedGeometry(snap.getDefiningLine(), options?.defaultElevation);
  }

  return (
    RelativeBearingSnapDerivedGeometry.tryCreate(snap, options) ??
    new AbsoluteSnapDerivedGeometry(snap.getDefiningLine(), options?.defaultElevation)
  );
}

export interface SnapDerivedGeometry {
  asPlaneGeometryProperties(): Pick<RoofPlane, 'flipped' | 'geometry'>;
  asFloorPolylineSegment(): FloorPolylineSegmentGeometry;
}

class RelativeBearingSnapDerivedGeometry implements SnapDerivedGeometry {
  flipped: boolean;
  bearingEnvelopeGuid: string;

  constructor(bearingEnvelopeGuid: string, flipped: boolean) {
    this.flipped = flipped;
    this.bearingEnvelopeGuid = bearingEnvelopeGuid;
  }

  /**
   * Attempts to create a bearing envelope-based roof plane relative geometry from a picked entity snap target. Returns
   *   null if the picked line is not suitable.
   */
  static tryCreate(
    pickedSnap: EntitySnapTarget,
    options: {
      primaryBearingSide: 'front' | 'back';
      bearingEnvelopes: Record<string, BearingEnvelope>;
    },
  ): RelativeBearingSnapDerivedGeometry | null {
    if (!isBearingEnvelopeSnapType(pickedSnap.type) || pickedSnap.originObjectGuid == null) return null;

    const bearingEnvelope = options.bearingEnvelopes[pickedSnap.originObjectGuid];
    if (bearingEnvelope == null) throw new Error('Unable to find bearing envelope for picked line.');

    const definingSegment = getEnvelopeSegment(bearingEnvelope);
    if (definingSegment == null) throw new Error('Unable to find defining segment for picked bearing envelope.');

    // roof planes are infinite until cutting occurs, so we can work exclusively with lines here
    const pickedLine = pickedSnap.getDefiningLine();
    const frontLine = getEnvelopeSegmentFromDefiningSegment(definingSegment, bearingEnvelope, 'Front').toLine();
    const backLine = getEnvelopeSegmentFromDefiningSegment(definingSegment, bearingEnvelope, 'Back').toLine();

    let flipped = false;

    if (pickedLine.equals(frontLine)) {
      if (options.primaryBearingSide !== 'front') flipped = true;
    } else if (pickedLine.equals(backLine)) {
      if (options.primaryBearingSide !== 'back') flipped = true;
    } else {
      return null; // an end segment or bevel cut segment was picked, which we can't use to create a roof plane
    }

    return new RelativeBearingSnapDerivedGeometry(pickedSnap.originObjectGuid, flipped);
  }

  asPlaneGeometryProperties() {
    return {
      flipped: this.flipped,
      geometry: {
        type: 'BearingEnvelope' as const,
        bearingEnvelopeGuid: this.bearingEnvelopeGuid,
      },
    };
  }

  asFloorPolylineSegment() {
    return {
      type: 'AgainstBearing' as const,
      bearingEnvelopeGuid: this.bearingEnvelopeGuid,
      flipped: this.flipped,
    };
  }
}

class AbsoluteSnapDerivedGeometry implements SnapDerivedGeometry {
  isInfinite: boolean;
  segment: LineSegment2DInterface;

  // only relevant for roof planes
  #defaultElevation: number | null;

  constructor(snapDefinition: LineSegment2D | Line2D, defaultElevation?: number) {
    if (snapDefinition instanceof Line2D) {
      this.isInfinite = true;
      this.segment = {
        basePoint: snapDefinition.basePoint.toObject(),
        endPoint: snapDefinition.getPointAlongLine(100).toObject(),
      };
    } else {
      this.isInfinite = false;
      this.segment = snapDefinition.toObject();
    }

    this.#defaultElevation = defaultElevation ?? null;
  }

  asPlaneGeometryProperties() {
    return {
      flipped: false,
      geometry: { ...this, elevation: this.#defaultElevation ?? 0, type: 'Absolute' as const },
    };
  }

  asFloorPolylineSegment() {
    return { ...this, type: 'AbsoluteVertical' as const };
  }
}
